import React, { Suspense, useEffect, useState } from "react";

import homePagePayload from "../../config/homePayload";
import homePageAPI from "../../api/homePageApi";

import CouponFlash from "./CouponFlash";
import { Link } from "react-router-dom";
import { Row, Col, Container } from "react-bootstrap";

import {
  Swiper,
  SwiperSlide,
  Autoplay,
  Navigation,
  Pagination,
} from "./Shared/CustomSwiper";
import { FaBox, FaCheckCircle, FaHeadset } from "react-icons/fa";
import {
  ArrowRight,
  FlaskConical,
  HeartPulse,
  Leaf,
  ShieldCheck,
} from "lucide-react";

const Product = React.lazy(() => import("../products/Card"));
const Testimonials = React.lazy(() => import("../testimonials/Card"));
const CategoryCard = React.lazy(() => import("../categories/Card"));
const Blog = React.lazy(() => import("../blogs/Card"));
const Banner = React.lazy(() => import("./Banner"));

function Home() {
  const [coupons, setCoupons] = useState([]);
  const [categories, setCategories] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [products, setProducts] = useState([]);
  const [topSellerProducts, setTopSellerProducts] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [topBanners, setTopBanners] = useState([]);
  const [middleBanners, setMiddleBanners] = useState([]);
  const firstMiddleBanner = middleBanners[0];
  const secondMiddleBanner = middleBanners[1];

  useEffect(() => {
    fetchHomePageDetails();
  }, []);

  const CACHE_TIME = 3 * 60 * 1000;

  const applyData = (data) => {
    setTopBanners(data?.banners?.filter((b) => b.position === "top"));
    setMiddleBanners(data?.banners?.filter((b) => b.position === "middle"));
    setCoupons(data?.coupons);
    setCategories(data?.categories);
    setTestimonials(data?.testimonials);
    setBlogs(data?.blogs);
    setProducts(data?.products);
    setTopSellerProducts(data?.topSellerProducts);
  };

  const fetchHomePageDetails = async () => {
    try {
      const cached = sessionStorage.getItem("homeData");
      const parsed = cached ? JSON.parse(cached) : null;

      if (parsed) applyData(parsed.data);

      if (!parsed || Date.now() - parsed.timestamp > CACHE_TIME) {
        const res = await homePageAPI.homePage(homePagePayload);
        if (res.code === 200) {
          sessionStorage.setItem(
            "homeData",
            JSON.stringify({ data: res.data, timestamp: Date.now() }),
          );
          if (!parsed) applyData(res.data); // only update state if no cache was shown
        }
      }
    } catch (error) {
      console.error("Home API Error:", error);
    }
  };
const brandFeatures = [
  {
    id: 1,
    icon: <Leaf size={32} strokeWidth={1.5} />,
    title: "100% Herbal Ingredients",
    desc: "Crafted with nature's finest herbs like Neem, Tulsi, Aloe Vera, Turmeric, and other botanical extracts for gentle daily care.",
  },
  {
    id: 2,
    icon: <ShieldCheck size={32} strokeWidth={1.5} />,
    title: "Ayurvedic Formulation",
    desc: "Inspired by traditional Ayurvedic wisdom and blended with carefully selected herbs to naturally nourish your skin and hair.",
  },
  {
    id: 3,
    icon: <FlaskConical size={32} strokeWidth={1.5} />,
    title: "Free From Harmful Chemicals",
    desc: "Made without parabens, sulfates, mineral oils, silicones, or artificial colors for a clean and gentle beauty routine.",
  },
  {
    id: 4,
    icon: <HeartPulse size={32} strokeWidth={1.5} />,
    title: "Safe For Everyday Use",
    desc: "Gentle formulations suitable for all skin and hair types, helping maintain healthy, naturally radiant beauty with regular use.",
  },
];

  return (
    <>
      {/* show banners */}
      {topBanners && topBanners.length > 0 && (
        <Swiper
          modules={[Navigation, Pagination, Autoplay]}
          slidesPerView={1}
          pagination={{ clickable: true }}
          autoplay={{ delay: 3000 }}
          loop={true}
        >
          {topBanners.map((banner, index) => (
            <SwiperSlide key={index}>
              <Banner item={banner} />
            </SwiperSlide>
          ))}
        </Swiper>
      )}

      {/* Coupon Bar */}
      {coupons && coupons.length > 0 && (
        <div className="coupon-section py-4">
          <div className="container-fluid px-lg-5 px-3">
            <Suspense fallback={<div></div>}>
              {coupons.map((coupon, index) => (
                <React.Fragment key={index}>
                  <CouponFlash item={coupon} />
                </React.Fragment>
              ))}
            </Suspense>
          </div>
        </div>
      )}

      {/* category listing */}
      {categories && categories.length > 0 && (
        <section className="custom-category-section main-section">
          <div className="container-fluid px-lg-5 px-3">
            <div className="d-flex align-items-center justify-content-between mb-md-4 mb-2">
              <h2 className="section-title">Shop by Category</h2>
              <Link to="/categories" className="view-all-btn">
                View All
              </Link>
            </div>
            <Swiper
              modules={[Navigation, Pagination, Autoplay]}
              slidesPerView={1}
              pagination={{ clickable: true }}
              autoplay={{ delay: 3000 }}
              spaceBetween={10}
              breakpoints={{
                0: { slidesPerView: 1.2 },
                768: { slidesPerView: 3 },
                1024: { slidesPerView: 4 },
              }}
              loop={true}
            >
              {categories.map((category, index) => (
                <SwiperSlide key={index}>
                  <CategoryCard item={category} />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </section>
      )}

      {/* middle banners */}
      {firstMiddleBanner && (
        <section className="custom-result-section">
          <div className="container-fluid px-lg-5 px-3">
            <div className="row align-items-center g-3">
              <div className="col-lg-6">
                <div className="custom-result-image-wrapper">
                  <img
                    src={firstMiddleBanner.image}
                    alt="Health "
                    className="custom-result-img"
                  />
                </div>
              </div>

              {/* RIGHT: Skincare Results & CTA */}
              <div className="col-lg-6 ps-lg-5">
                <div className="custom-result-content">
                  <div className="custom-result-tag">
                    <Leaf size={16} className="me-2" />
                    <span>100% NATURAL INGREDIENTS</span>
                  </div>

                  <h2 className="custom-result-h2">
                    {firstMiddleBanner.title}
                  </h2>

                  <p className="custom-result-p">
                    {firstMiddleBanner.subTitle}
                  </p>

                  <Link to="/shop" className="btn custom-result-cta-btn">
                    DISCOVER MORE <ArrowRight size={18} className="ms-2" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* bestsellers */}
      {products && products.length > 0 && (
        <section className="bestsellers-section main-section">
          <div className="container-fluid px-lg-5 px-3 ">
            <div className="d-flex align-items-center justify-content-between mb-md-4 mb-2">
              <h2 className="section-title">Bestsellers</h2>
              <Link to="/shop" className="view-all-btn">
                View All
              </Link>
            </div>
            <Row className="g-2">
              <Suspense fallback={<div></div>}>
                {products.map((product, index) => (
                  <Col lg={3} md={6} sm={6} xs={6} key={[product._id || index]}>
                    <Product item={product} />
                  </Col>
                ))}
              </Suspense>
            </Row>
          </div>
        </section>
      )}

      {/* cta section */}
      {secondMiddleBanner && (
        <section
          className="cta-banner-2"
          style={{
            backgroundImage: `url(${secondMiddleBanner.image})`,
          }}
        >
          <div className="cta-banner-overlay"></div>

          <Container>
            <Row className="justify-content-center">
              <Col lg={12}>
                <div className="banner-content text-center">
                  <span className="banner-subheading">
                    Premium Herbal Collection
                  </span>

                  <h2 className="banner-heading">{secondMiddleBanner.title}</h2>

                  <p className="banner-description">
                    {secondMiddleBanner.subTitle ||
                      secondMiddleBanner.sub_title}
                  </p>
                  <div className="banner-btn-wrapper mt-4">
                    <Link to="/shop" className="banner-btn">
                      Explore Collection
                      <ArrowRight size={18} className="ms-2" />
                    </Link>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </section>
      )}

      {/* trending */}
      {topSellerProducts && topSellerProducts.length > 0 && (
        <section className="bestsellers-section main-section mt-4">
          <div className="container-fluid px-lg-5 px-3 ">
            <div className="d-flex align-items-center justify-content-between mb-md-4 mb-2">
              <h2 className="section-title ">Trending Products</h2>
              <Link to="/shop" className="view-all-btn">
                View All
              </Link>
            </div>
            <Row className="g-2">
              <Suspense fallback={<div></div>}>
                {topSellerProducts.map((product, index) => (
                  <Col lg={3} md={6} sm={6} xs={6} key={[product._id || index]}>
                    <Product item={product} />
                  </Col>
                ))}
              </Suspense>
            </Row>
          </div>
        </section>
      )}

      {testimonials && testimonials.length > 0 && (
        <section className="testimonials-section py-5">
          <Container fluid className="px-lg-5 px-3">
            <div className="text-left mb-md-4 mb-2">
              <h2 className="section-title">What Our Clients Say</h2>
            </div>

            <Swiper
              modules={[Navigation, Autoplay]}
              spaceBetween={20}
              slidesPerView={1}
              navigation={true}
              autoplay={{ delay: 2500 }}
              breakpoints={{
                0: { slidesPerView: 1.2 },
                768: { slidesPerView: 2 },
                1024: { slidesPerView: 3 },
              }}
              className="testimonial-swiper"
            >
              <Suspense fallback={<div></div>}>
                {testimonials.map((testimonial, index) => (
                  <SwiperSlide key={index}>
                    <Testimonials item={testimonial} />
                  </SwiperSlide>
                ))}
              </Suspense>
            </Swiper>
          </Container>
        </section>
      )}

      {blogs && blogs.length > 0 && (
        <section className="blog-section py-5">
          <Container fluid className="px-lg-5 px-3">
            <div className="d-flex justify-content-between align-items-end mb-md-4 mb-2">
              <div>
                <h2 className="mt-2 mb-0 section-title">
                  Real Results from Real People
                </h2>
              </div>
              <Link to="/blog" className="view-all-link">
                View All Blogs
              </Link>
            </div>

            <Swiper
              modules={[Pagination, Autoplay]}
              spaceBetween={10}
              slidesPerView={1}
              // autoplay={{ delay: 3500 }}
              pagination={{ clickable: true }}
              breakpoints={{
                768: { slidesPerView: 2 },
                1024: { slidesPerView: 3 },
              }}
              className="blog-swiper"
            >
              <Suspense fallback={<div></div>}>
                {blogs.map((blog, index) => (
                  <SwiperSlide key={blog.id}>
                    <Blog item={blog} />
                  </SwiperSlide>
                ))}
              </Suspense>
            </Swiper>
          </Container>
        </section>
      )}

      {/* featured section */}
      <section className="custom-features-section py-5">
        <div className="container-fluid px-lg-5 px-3">
          {/* Simple Minimal Header */}
          <div className="row mb-3 align-items-end">
            <div className="col-md-8">
              <h2 className="section-title">Why Your Skin Loves Us</h2>
            </div>
          </div>

          <div className="row g-2 justify-content-center">
            {brandFeatures.map((feature) => (
              <div className="col-lg-3 col-md-6 col-6" key={feature.id}>
                <div className="custom-features-card">
                  <div className="custom-features-icon-wrap">
                    {feature.icon}
                  </div>
                  <h4 className="custom-features-title">{feature.title}</h4>
                  <p className="custom-features-desc">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

export default Home;
